import type {
  ConfiguratorV2CsticTargetRequest,
  ConfiguratorV2StepResetRequest,
  ConfiguratorV2StepResetScope,
} from "../../data/configuratorV2Api.types";
import type {
  ConfiguratorDefinition,
  ControlDefinition,
  StepResetScope,
  SubstepDefinition,
} from "../../contract/definition";
import type { ConfiguratorConfiguration, ConfiguratorInstanceContext } from "../../contract/configuration";
import { resolveInstanceContexts } from "../instanceContext";

const DEFAULT_SCOPE: StepResetScope = "stepAndSubsequent";

const toApiScope = (scope: StepResetScope): ConfiguratorV2StepResetScope =>
  scope === "stepOnly" ? "STEP_ONLY" : "STEP_AND_SUBSEQUENT";

const getControls = (substep: SubstepDefinition): readonly ControlDefinition[] =>
  substep.renderer === "carouselColumns"
    ? substep.columns.flatMap((column) => [column.carousel, ...(column.details ?? [])])
    : substep.renderer === "configurationAttachment"
    ? []
    : substep.controls;

const collectControlTargets = (
  control: ControlDefinition,
  context: ConfiguratorInstanceContext,
): ConfiguratorV2CsticTargetRequest[] => {
  const csticNames = [
    control.cstic,
    ...(control.type === "TileSelect"
      ? (control.tiles ?? []).flatMap((tile) => tile.writes.map(({ cstic }) => cstic))
      : []),
  ];

  return csticNames.map((csticName) => ({ instanceId: context.instance.id, csticName }));
};

export const buildStepResetRequest = (
  definition: ConfiguratorDefinition,
  configuration: ConfiguratorConfiguration,
  stepIndex: number,
): ConfiguratorV2StepResetRequest => {
  if (stepIndex < 0 || stepIndex >= definition.steps.length) {
    throw new RangeError(`Step index ${stepIndex} is outside the configurator definition.`);
  }

  const scope = definition.stepResetScope ?? DEFAULT_SCOPE;
  const steps =
    scope === "stepOnly" ? definition.steps.slice(stepIndex, stepIndex + 1) : definition.steps.slice(stepIndex);
  const targets = new Map<string, ConfiguratorV2CsticTargetRequest>();

  steps.forEach((step) => {
    step.substeps.forEach((substep) => {
      resolveInstanceContexts(configuration, substep.instanceTarget).forEach((context) => {
        getControls(substep).forEach((control) => {
          collectControlTargets(control, context).forEach((target) => {
            targets.set(`${target.instanceId}\u0000${target.csticName}`, target);
          });
        });
      });
    });
  });

  return {
    configId: configuration.configId,
    stepNumber: stepIndex + 1,
    scope: toApiScope(scope),
    targets: [...targets.values()],
  };
};
